import Grid from '@mui/material/Grid';
import React, { FC, useMemo } from 'react';
import TwSelect, { TwSelectOption } from '../TwSelects/TwSelects';
import TwFileVisibilitySelect from '../TwFileVisibilitySelect/TwFileVisibilitySelect';
import CloseIcon from '@mui/icons-material/Close';
import { LoadDocumentType } from '../../redux/api/load/load.model';
import FileUtils from '../../utils/fileUtils';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import Box from '@mui/material/Box';
import NumberUtils from '../../utils/numberUtils';
import Stack from '@mui/material/Stack';
import { Typography } from '@mui/material';
import { OrdersTripsDocumentParams } from '../../redux/api/orders/orders.model';
import useAuth from '../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../providers/User/User.model';
import { EntityType } from '../../redux/Global.model';
import { useAppSelector } from '../../redux/hooks';

interface TwUploadDocumentsRowsProps {
  key: string | number;
  entity: 'order' | 'trip';
  entityIdsSelectList?: TwSelectOption[];
  documentTypeList?: TwSelectOption[];
  file: OrdersTripsDocumentParams;
  hideEntitySelector: boolean;
  isPublicLink?: boolean;
  handleVisibilityChange: (name: string, visibility: boolean) => void;
  handleTypeChange: (name: string, type: LoadDocumentType) => void;
  handleIdSelector: (name: string, id: string) => void;
  handleRemoveFile: (name: File) => void;
}

const TwUploadDocumentsRows: FC<TwUploadDocumentsRowsProps> = ({
  entityIdsSelectList,
  documentTypeList,
  file,
  entity,
  hideEntitySelector,
  isPublicLink,
  handleVisibilityChange,
  handleTypeChange,
  handleIdSelector,
  handleRemoveFile
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const ltlEnabledLoggedInUsers = userData?.tenant?.ltl_enabled || false;
  const isLtlEnabledLink = useAppSelector((state) => state.user.isLtlEnabled);
  const isDriver = userData?.role === USER_ROLE.DRIVER;

  const disableEntitySelector = useMemo(() => {
    if (ltlEnabledLoggedInUsers || isLtlEnabledLink) {
      return entityIdsSelectList?.length === 0;
    } else {
      return entity === 'trip';
    }
  }, [ltlEnabledLoggedInUsers, isLtlEnabledLink, entity, entityIdsSelectList?.length]);

  return (
    <Grid container spacing={1} className="tw-upload-ot-files__row">
      <Grid item xs={!hideEntitySelector ? 4 : 6} sx={{ display: 'flex' }}>
        <Box className="tw-upload-ot-files__image">
          {file.document.type.match('image/') ? (
            <img
              src={FileUtils.createTemporarySRCThumbnail(file.document)}
              width={40}
              height={40}
            />
          ) : (
            <InsertDriveFileOutlinedIcon />
          )}
        </Box>
        <Stack className="tw-upload-ot-files__document-info" flexDirection={'column'}>
          <Typography>{file.document.name}</Typography>
          <Typography component={'span'}>{NumberUtils.getFileSize(file.document.size)}</Typography>
        </Stack>
      </Grid>
      <Grid item xs={!hideEntitySelector ? 4 : 5}>
        <TwSelect
          id="attachmentType"
          name="attachmentType"
          value={file.type}
          optionList={documentTypeList || []}
          onInputChange={(e): void =>
            handleTypeChange(file.document.name, e.target.value as LoadDocumentType)
          }
        />
      </Grid>
      {!hideEntitySelector && (
        <Grid item xs={2}>
          <TwSelect
            id="idSelector"
            name="idSelector"
            value={entity === 'order' ? file.tripId || '' : file.orderId || ''}
            optionList={entityIdsSelectList || []}
            onInputChange={(e): void =>
              handleIdSelector(file.document.name, e.target.value as string)
            }
            disabled={disableEntitySelector}
          />
        </Grid>
      )}
      <Grid item xs={!hideEntitySelector ? 2 : 1}>
        <Stack flexDirection={'row'} alignItems={'center'}>
          {!isDriver && !isPublicLink && (
            <TwFileVisibilitySelect
              name="visibility"
              value={file.visibility ? 'visible' : 'hidden'}
              onInputChange={(e): void =>
                handleVisibilityChange(
                  file.document.name,
                  e.target.value === 'visible' ? true : false
                )
              }
            />
          )}
          <CloseIcon
            sx={{ color: '#9ca3af', cursor: 'pointer' }}
            onClick={(): void => handleRemoveFile(file.document)}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};

export default TwUploadDocumentsRows;
